import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import { focusRing } from '../styles/focus-ring';
import { typographyVariant } from '../typography';
import type { FlushableVariant } from './button.types';

/** Variants that support the flush prop — keep in sync with FlushableVariant type. */
const FLUSHABLE_VARIANTS: FlushableVariant[] = [
  'ghost',
  'panel_ghost',
  'destructive_ghost',
  'success_ghost',
];

export const variantClasses = {
  default: cn(
    'bg-background-button-primary text-text-primary-invert hover:bg-background-button-primary-hover active:bg-background-button-primary-pressed disabled:bg-background-button-primary-disabled'
  ),
  secondary: cn(
    'bg-background-secondary text-text-primary hover:bg-background-secondary-hover active:bg-background-secondary-pressed disabled:bg-background-secondary-disabled disabled:text-text-disabled'
  ),
  outline: cn(
    'border border-outline bg-transparent text-text-primary hover:bg-background-tertiary-hover hover:text-text-primary active:bg-background-tertiary-pressed disabled:text-text-disabled'
  ),
  // @deprecated Use `outline` instead.
  surface: cn(
    'border border-outline bg-surface text-text-primary hover:bg-surface/50 hover:text-text-primary active:bg-surface/40 disabled:bg-surface-disabled disabled:text-text-disabled',
    // newBrandDark: `bg-surface` is the near-black elevated surface; use the page
    // bg so the button reads as a neutral filled control against the dark page.
    'newBrandDark:bg-surface-page newBrandDark:hover:bg-surface-page/50 newBrandDark:active:bg-surface-page/40'
  ),
  ghost: cn(
    'hover:bg-background-tertiary-hover hover:text-text-primary active:bg-background-tertiary-pressed disabled:text-text-disabled'
  ),
  // @deprecated Use `ghost` instead. Removed once
  // `release-scribe-enable-new-session-list` is at 100% and
  // `IS_SIDEBAR_PANEL_ENABLED` is retired
  panel_ghost: cn(
    'bg-transparent text-text-primary',
    'hover:bg-background-secondary hover:text-text-primary',
    'active:bg-background-secondary-pressed',
    'data-[active=true]:bg-background-secondary data-[active=true]:text-text-primary',
    'data-[active=true]:hover:bg-background-secondary-hover',
    'data-[active=true]:active:bg-background-secondary-pressed',
    'disabled:bg-transparent disabled:text-text-disabled',
    'data-[active=true]:disabled:bg-background-secondary-disabled data-[active=true]:disabled:text-text-disabled'
  ),
  success: cn(
    'bg-validation-success text-text-primary-invert hover:bg-validation-success-hover active:bg-validation-success-pressed  disabled:bg-validation-success-disabled'
  ),
  success_ghost: cn(
    'text-validation-success hover:bg-validation-success-muted active:bg-validation-success-muted active:text-validation-success-pressed disabled:text-validation-success-disabled'
  ),
  destructive_ghost: cn(
    'text-validation-destructive hover:bg-validation-destructive-muted active:bg-validation-destructive-muted active:text-validation-destructive-pressed disabled:text-validation-destructive-disabled'
  ),
  destructive: cn(
    'bg-validation-destructive text-text-primary-invert hover:bg-validation-destructive-hover active:bg-validation-destructive-pressed disabled:bg-validation-destructive-disabled'
  ),
  destructive_soft: cn(
    'bg-validation-destructive-muted text-validation-destructive hover:bg-validation-destructive/15 active:bg-validation-destructive/20 disabled:bg-validation-destructive-muted disabled:text-validation-destructive-disabled'
  ),
  // @deprecated Use `destructive_soft` for a destructive action; for an
  // error-state field border, use the control's invalid state instead.
  destructive_outline_with_border: cn(
    'border border-validation-destructive bg-surface text-validation-destructive hover:bg-validation-destructive-muted active:bg-validation-destructive-muted active:text-validation-destructive-pressed disabled:border-validation-destructive-disabled disabled:text-validation-destructive-disabled'
  ),
  link: cn(
    'text-text-link underline-offset-4 hover:text-text-link-hover hover:underline hover:shadow-none active:text-text-link-pressed disabled:text-text-disabled'
  ),
  // White by default; the dark tier override flips it to black so it reads on the
  // light fill.
  pro: cn(
    'bg-tier-pro text-text-primary-on-dark hover:bg-tier-pro-hover disabled:bg-tier-pro-disabled [.skiesTierDark_&]:text-text-primary-invert'
  ),
  // pro_plus is the second (practice) tier, so its fill reads from tier-practice.
  pro_plus: cn(
    'bg-tier-practice text-text-primary-on-dark hover:bg-tier-practice-hover disabled:bg-tier-practice-disabled [.skiesTierDark_&]:text-text-primary-invert'
  ),
} as const;

export const sizeClasses = {
  /** @deprecated Use `xs` instead (DS-1573). 24px is the floor of WCAG 2.5.8. */
  xxs: cn('h-6 min-w-6 gap-x-1 rounded-md px-2 py-1.5'),
  xs: cn('h-7 min-w-7 gap-x-1 rounded-md px-2.5 py-2'),
  sm: cn('h-8 min-w-8 gap-x-1 rounded-md px-2.5 py-[9px]'),
  md: cn('h-9 min-w-9 gap-x-1.5 rounded-md px-3 py-2.5'),
  lg: cn('h-10 min-w-10 gap-x-1.5 rounded-lg px-3 py-[11px]'),
  /**
   * 44px — the iOS HIG and WCAG 2.5.5 minimum target size. Use for any control
   * a finger will hit: mobile surfaces, tablet-facing flows, device-pairing
   * screens. Applies to secondary actions in the same stack too — a ghost
   * "Cancel" needs the same target as the primary beside it.
   */
  touch: cn('h-11 min-w-11 gap-x-2 rounded-lg px-4 py-3'),
} as const;

export const buttonVariants = cva(
  cn(
    'm-0 inline-flex cursor-pointer items-center justify-center gap-x-1 whitespace-nowrap text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:cursor-not-allowed',
    focusRing
  ),
  {
    variants: {
      variant: variantClasses,
      size: sizeClasses,
      iconOnly: { true: '', false: '' },
      disableAutomaticTypographyClasses: { false: '', true: '' },
      disableAutomaticSvgSizing: { false: '', true: '' },
      disabled: { true: 'pointer-events-none', false: '' },
      flush: {
        all: '',
        horizontal: '',
        vertical: '',
        start: '',
        end: '',
        top: '',
        bottom: '',
      },
    },
    compoundVariants: [
      // Icon only compound variants
      {
        iconOnly: true,
        size: 'xxs',
        className: 'w-6 px-1.5',
      },
      {
        iconOnly: true,
        size: 'xs',
        className: 'w-7 px-2',
      },
      {
        iconOnly: true,
        size: 'sm',
        className: 'w-8 px-[9px]',
      },
      {
        iconOnly: true,
        size: 'md',
        className: 'w-9 px-2.5',
      },
      {
        iconOnly: true,
        size: 'lg',
        className: 'w-10 px-[11px]',
      },
      {
        iconOnly: true,
        size: 'touch',
        className: 'w-11 px-3',
      },

      // Automatic typography classes compound variants
      {
        disableAutomaticTypographyClasses: false,
        size: 'xxs',
        className: cn(
          typographyVariant({
            variant: 'captionBold',
            className: 'leading-6',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'xs',
        className: cn(
          typographyVariant({
            variant: 'captionBold',
            className: 'leading-7',
          })
        ),
      },

      {
        disableAutomaticTypographyClasses: false,
        size: 'sm',
        className: cn(
          typographyVariant({
            variant: 'p2Bold',
            className: 'leading-8',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'md',
        className: cn(
          typographyVariant({
            variant: 'p2Bold',
            className: 'leading-9',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'lg',
        className: cn(
          typographyVariant({
            variant: 'pBold',
            className: 'leading-10',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'touch',
        className: cn(
          typographyVariant({
            variant: 'h5',
            className: 'leading-11',
          })
        ),
      },

      // Automatic SVG sizing compound variants
      {
        disableAutomaticSvgSizing: false,
        size: 'xxs',
        className: cn('[&_svg]:size-3 [&_svg]:stroke-icon-xxs'),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'xs',
        className: cn('[&_svg]:size-3 [&_svg]:stroke-icon-xs'),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'sm',
        className: cn('[&_svg]:size-3.5 [&_svg]:stroke-icon-sm'),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'md',
        className: cn('[&_svg]:size-4 [&_svg]:stroke-icon-md'),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'lg',
        className: cn('[&_svg]:size-4.5 [&_svg]:stroke-icon-lg'),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'touch',
        className: cn('[&_svg]:size-5 [&_svg]:stroke-icon-xl'),
      },
      // Disabled state compound variants - this is only used when calling buttonVariants() directly
      {
        disabled: true,
        variant: 'default',
        className: 'bg-background-button-primary-disabled',
      },
      {
        disabled: true,
        variant: 'secondary',
        className: 'bg-background-secondary-disabled text-text-disabled',
      },
      {
        disabled: true,
        variant: 'outline',
        className: 'text-text-disabled',
      },
      {
        disabled: true,
        variant: 'surface',
        className: 'bg-surface-disabled text-text-disabled',
      },
      {
        disabled: true,
        variant: 'ghost',
        className: 'text-text-disabled',
      },
      {
        disabled: true,
        variant: 'panel_ghost',
        className:
          'bg-transparent text-text-disabled data-[active=true]:bg-background-secondary-disabled data-[active=true]:text-text-disabled',
      },
      {
        disabled: true,
        variant: 'success',
        className: 'bg-validation-success-disabled',
      },
      {
        disabled: true,
        variant: 'success_ghost',
        className: 'text-validation-success-disabled',
      },
      {
        disabled: true,
        variant: 'destructive_ghost',
        className: 'text-validation-destructive-disabled',
      },
      {
        disabled: true,
        variant: 'destructive',
        className: 'bg-validation-destructive-disabled',
      },
      {
        disabled: true,
        variant: 'destructive_soft',
        className: 'bg-validation-destructive-muted text-validation-destructive-disabled',
      },
      {
        disabled: true,
        variant: 'destructive_outline_with_border',
        className:
          'border-validation-destructive-disabled text-validation-destructive-disabled',
      },
      {
        disabled: true,
        variant: 'link',
        className: 'text-text-disabled',
      },
      {
        disabled: true,
        variant: 'pro',
        className: 'bg-tier-pro-disabled',
      },
      {
        disabled: true,
        variant: 'pro_plus',
        className: 'bg-tier-practice-disabled',
      },

      // Flush compound variants
      // Applies negative margins to ghost buttons for optical alignment with content
      // Uses -mx/-my to match px/py padding where possible

      // Flush all sides
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mx-1.5 -my-1',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mx-2.5 -my-2',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-mx-2.5 [margin-block:-9px]',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mx-3 -my-2.5',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-mx-3 [margin-block:-11px]',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mx-4 -my-3',
      },

      // Flush horizontal (left and right)
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mx-1.5',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mx-2.5',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-mx-2.5',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mx-3',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-mx-3',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mx-4',
      },

      // Flush vertical (top and bottom)
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-my-1',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-my-2',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '[margin-block:-9px]',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-my-2.5',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '[margin-block:-11px]',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-my-3',
      },

      // Flush start (left/inline-start)
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-ms-1.5',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-ms-2.5',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-ms-2.5',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-ms-3',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-ms-3',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-ms-4',
      },

      // Flush end (right/inline-end)
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-me-1.5',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-me-2.5',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-me-2.5',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-me-3',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-me-3',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-me-4',
      },

      // Flush top
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mt-1',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mt-2',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: 'mt-[-9px]',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mt-2.5',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: 'mt-[-11px]',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mt-3',
      },

      // Flush bottom
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mb-1',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mb-2',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: 'mb-[-9px]',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mb-2.5',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: 'mb-[-11px]',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mb-3',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'lg',
      disableAutomaticTypographyClasses: false,
      disableAutomaticSvgSizing: false,
    },
  }
);
